import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { StarIcon, ArrowLeftIcon } from 'lucide-react';
export function HeroSection() {
  const [activeRow, setActiveRow] = useState(0);
  // Auto-cycle table rows
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveRow(prev => (prev + 1) % 3);
    }, 3000);
    return () => clearInterval(interval);
  }, []);
  const businesses = [{
    name: 'The coffee bean & tea leaf',
    owner: 'J. Lee, CEO',
    email: 'jlee@coffeebean.com',
    techStack: 'Shopify, Stripe',
    rating: 4.5,
    category: 'Cafe'
  }, {
    name: 'Sunset boulevard yoga studio',
    owner: 'M. Chen, Owner',
    email: 'mchen@sunsetyoga.com',
    techStack: 'Mindbody, Square',
    rating: 4.9,
    category: 'Fitness'
  }, {
    name: "Mama mia's pizzeria",
    owner: 'R. Rossi, Owner',
    email: 'rossi@mamamias.com',
    techStack: 'Toast, DoorDash',
    rating: 4.7,
    category: 'Restaurant'
  }];
  return <section className="relative pt-16 pb-24 overflow-hidden bg-white" data-id="element-2516">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-2517">
        <a href="#/features" className="inline-flex items-center gap-2 text-sm font-semibold text-slate-500 hover:text-slate-900 transition-colors mb-8" data-id="element-2518">
          <ArrowLeftIcon className="w-4 h-4" data-id="element-2519" /> All features
        </a>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center" data-id="element-2520">
          {/* Left Column: Copy & CTAs */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} className="max-w-2xl" data-id="element-2521">
            <h1 className="text-6xl sm:text-7xl lg:text-[5.5rem] font-bold tracking-tight text-slate-900 mb-8 leading-[1.05]" data-id="element-2522">
              Local <br data-id="element-2523" />
              <span className="text-blue-600" data-id="element-2524">business</span> <br data-id="element-2525" />
              data API
            </h1>

            <p className="text-xl sm:text-2xl text-slate-600 mb-8 max-w-lg leading-relaxed" data-id="element-2526">
              The local business API and local business search API built for
              scale. Get{' '}
              <span className="inline-flex items-center px-4 py-1 rounded-full bg-blue-50 text-blue-700 text-lg sm:text-xl font-medium mx-1" data-id="element-2527">
                owner emails
              </span>
              ,{' '}
              <span className="inline-flex items-center px-4 py-1 rounded-full bg-green-50 text-green-700 text-lg sm:text-xl font-medium mx-1" data-id="element-2528">
                tech stack
              </span>
              , and{' '}
              <span className="inline-flex items-center px-4 py-1 rounded-full bg-purple-50 text-purple-700 text-lg sm:text-xl font-medium mx-1 mt-2 sm:mt-0" data-id="element-2529">
                local business info
              </span>{' '}
              in 15 minutes with our API.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 mb-10" data-id="element-2530">
              <a href="http://app.openmart.com/register" target="_blank" rel="noopener noreferrer" className="px-10 py-4 text-white font-bold bg-blue-600 rounded-full hover:bg-blue-700 transition-colors text-lg text-center" data-id="element-2531">
                Try for free
              </a>
              <a href="https://api.openmart.ai/redoc" target="_blank" rel="noopener noreferrer" className="px-10 py-4 text-slate-700 font-medium bg-slate-50 rounded-full hover:bg-slate-100 transition-colors text-lg text-center" data-id="element-2532">
                Read the docs
              </a>
            </div>

            {/* Static Stats Bar */}
            <div className="flex gap-12 pt-8" data-id="element-2533">
              <div data-id="element-2534">
                <div className="text-3xl font-bold text-slate-900 mb-1" data-id="element-2535">
                  200M+
                </div>
                <div className="text-sm text-slate-500 font-medium" data-id="element-2536">
                  Local businesses across 10+ countries
                </div>
              </div>
              <div data-id="element-2537">
                <div className="text-3xl font-bold text-slate-900 mb-1" data-id="element-2538">
                  50+
                </div>
                <div className="text-sm text-slate-500 font-medium" data-id="element-2539">
                  Data fields
                </div>
              </div>
              <div data-id="element-2540">
                <div className="text-3xl font-bold text-slate-900 mb-1" data-id="element-2541">
                  99.9%
                </div>
                <div className="text-sm text-slate-500 font-medium" data-id="element-2542">
                  API uptime
                </div>
              </div>
            </div>
          </motion.div>

          {/* Right Column: Live Data Preview Card */}
          <div className="relative" data-id="element-2543">
            <div className="bg-slate-50 rounded-[2rem] p-8 sm:p-10 relative z-10" data-id="element-2544">
              <div className="flex justify-between items-center mb-8" data-id="element-2545">
                <h3 className="text-2xl font-bold text-slate-900" data-id="element-2546">
                  Live data preview
                </h3>
              </div>

              <div className="overflow-x-auto" data-id="element-2547">
                <table className="w-full text-sm text-left" data-id="element-2548">
                  <thead data-id="element-2549">
                    <tr className="text-slate-400 font-medium border-b border-slate-200/60" data-id="element-2550">
                      <th className="pb-4 pl-4 pr-4 font-medium" data-id="element-2551">
                        Local business
                      </th>
                      <th className="pb-4 px-4 font-medium" data-id="element-2552">Owner contact</th>
                      <th className="pb-4 pl-4 font-medium" data-id="element-2553">Tech stack</th>
                      <th className="pb-4 pl-4 font-medium hidden sm:table-cell" data-id="element-2554">
                        Rating
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200/60" data-id="element-2555">
                    {businesses.map((biz, idx) => <tr key={idx} className={`transition-all duration-500 relative ${activeRow === idx ? 'bg-white shadow-sm rounded-xl' : ''}`} data-id="element-2556">
                        <td className="py-6 pl-4 pr-4 font-medium text-slate-900 rounded-l-xl" data-id="element-2557">
                          {biz.name}
                          <div className="text-xs text-slate-500 font-normal mt-1 sm:hidden" data-id="element-2558">
                            {biz.category}
                          </div>
                        </td>
                        <td className="py-6 px-4 text-slate-600" data-id="element-2559">
                          <div className="whitespace-nowrap" data-id="element-2560">{biz.owner}</div>
                          <div className="text-xs text-blue-500 mt-0.5" data-id="element-2561">
                            {biz.email}
                          </div>
                        </td>
                        <td className="py-6 pl-4 text-blue-600 truncate max-w-[120px] sm:max-w-none" data-id="element-2562">
                          {biz.techStack}
                        </td>
                        <td className="py-6 pl-4 hidden sm:table-cell rounded-r-xl" data-id="element-2563">
                          <div className="flex items-center gap-1.5 text-slate-700 font-medium" data-id="element-2564">
                            <StarIcon className="w-4 h-4 fill-yellow-400 text-yellow-400" data-id="element-2565" />
                            {biz.rating}
                          </div>
                        </td>
                      </tr>)}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}